import React, { useCallback, useEffect, useState } from 'react';
import RequestListLayout from 'components/base/request-list-layout';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { Link } from 'react-router-dom';
import RequestListFilters from 'components/base/request-list-filters';
import ProcurementCard from 'components/base/procurement-card';
import { ReactComponent as NoProcurement } from 'assets/icons/no-procurement.svg';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { ProcurementParamsType, ProcurementType } from 'models/procurement.model';
import { ENUM_REQUEST_STATUS } from 'models/common.model';
import { debounce } from 'utils/functions';
import useDashboardContext from 'contexts/dashboard.context';
import { SocketIOConnection } from 'utils/socket-config';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

export type DateRangeType = {
  startDate: Date;
  endDate: Date;
};

type RequestStatusFilter = ENUM_REQUEST_STATUS;

type ProcurementFilters = {
  search: string;
  status: RequestStatusFilter[];
  dateRange: DateRangeType | null;
  current_page: number;
};

const ProcurementList = () => {
  const [isFetchingProcurements, setIsFetchingProcurements] = useState(false);
  const [procurements, setProcurements] = useState<ProcurementType[]>([]);
  const [lastPage, setLastPage] = useState(1);
  const [filters, setFilters] = useState<ProcurementFilters>({
    search: '',
    status: [],
    dateRange: null,
    current_page: 1
  });
  const { activeStore } = useDashboardContext();
  const { can } = useRoleContext();
  const canWriteProcurement = can(InventoryModule.Procurement, RolePermission.Write);

  const initSocket = async () => {
    const conn = await SocketIOConnection.getConnection();
    const roomName = `procurement_request:${activeStore?.id}`;

    conn.onReconnect(async () => {
      conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
    });

    conn.addEventListener(roomName, ' procurement_request_updated', (data: ProcurementType) => {
      setProcurements((prev) => {
        if (!prev) return [];
        const findRequest = prev?.find((item) => item.id === data.id);
        if (findRequest) {
          return prev?.map((item) => (item.id === data.id ? data : item));
        } else {
          return [...prev, data];
        }
      });
    });
    conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
    return () => {
      conn.removeListener(roomName, ' procurement_request_updated');
    };
  };
  const removeListener = async () => {
    const conn = await SocketIOConnection.getConnection();
    const roomName = `procurement_request:${activeStore?.id}`;
    conn.removeListener(roomName, ' procurement_request_updated');
  };
  useEffect(() => {
    if (!activeStore?.id) return;
    initSocket();
    return () => {
      removeListener();
    };
  }, []);

  const fetchProcurements = async (
    searchString: string,
    status: RequestStatusFilter[],
    current_page?: number,
    startDate?: Date,
    endDate?: Date
  ) => {
    if (!activeStore?.id) return;

    try {
      setIsFetchingProcurements(true);

      const normalizedStatus = status;
      const statusParam =
        normalizedStatus.length === 0
          ? undefined
          : normalizedStatus.length === 1
          ? normalizedStatus[0]
          : normalizedStatus;

      const params: ProcurementParamsType = {
        code: searchString,
        page: current_page || 1,
        size: 15,
        status: statusParam,
        store_id: activeStore?.id
      };

      if (startDate) {
        params.start_date = startDate.toISOString();
      }
      if (endDate) {
        params.end_date = endDate.toISOString();
      }

      const response = await apiService.get('procurement', {
        version: 'v2',
        params
      });
      if (response) {
        setProcurements(response.data.data);
        setLastPage(response.data.pagination.total / response.data.pagination.page_size);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching procurements'}
          type='error'
        />
      );
    } finally {
      setIsFetchingProcurements(false);
    }
  };

  const debounceFetchProcurements = useCallback(debounce(fetchProcurements, 1000), []);

  useEffect(() => {
    setIsFetchingProcurements(true);
    setProcurements([]);
    debounceFetchProcurements(
      filters.search,
      filters.status,
      filters.current_page,
      filters.dateRange?.startDate,
      filters.dateRange?.endDate
    );
  }, [filters, activeStore?.id]);

  return (
    <RequestListLayout
      title='Procurement'
      description='Manage inventory item requests'
      actions={
        canWriteProcurement ? (
          <Link to={MAIN_STORE_ROUTE_KEYS.NEW_PROCUREMENT}>
            <Button variant='block' color='primary' className='px-6 py-2 text-sm'>
              New Procurement
            </Button>
          </Link>
        ) : undefined
      }
      filters={
        <RequestListFilters
          statusMode='multi'
          statusOptions={[
            { text: ENUM_REQUEST_STATUS.REQUESTED, value: ENUM_REQUEST_STATUS.REQUESTED },
            { text: ENUM_REQUEST_STATUS.APPROVED, value: ENUM_REQUEST_STATUS.APPROVED },
            { text: ENUM_REQUEST_STATUS.CANCELLED, value: ENUM_REQUEST_STATUS.CANCELLED },
            { text: ENUM_REQUEST_STATUS.RECEIVED, value: ENUM_REQUEST_STATUS.RECEIVED }
          ]}
          filters={filters}
          onFilterChange={setFilters}
          pageCount={lastPage}
        />
      }>
      {isFetchingProcurements ? (
        <div className='pt-10'>
          <ActivityIndicator />
        </div>
      ) : procurements.length === 0 ? (
        <div className='grid place-items-center mt-24'>
          <NoProcurement className='mb-5' />
          <p className='font-medium text-[#5E6164]'>You are yet to make any procurement</p>
        </div>
      ) : (
        <div className='mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {procurements.map((p, i) => (
            <ProcurementCard key={i} procurement={p} />
          ))}
        </div>
      )}
    </RequestListLayout>
  );
};

export default ProcurementList;
